import type { PublicBranding } from '@/api/types/generated'

const API_BASE = ''

export type ClientViewer = {
  id: number
  client_space_id: number
  email: string
  first_name: string
  last_name: string
  role: 'lead' | 'viewer'
  has_password: boolean
  last_seen_at: string | null
  created_at: string
}

export type ActionType =
  | 'view'
  | 'shortlist'
  | 'unshortlist'
  | 'pass'
  | 'comment'
  | 'meeting_request'
  | 'rating'
  | 'flag'
  | 'custom_qa_request'
  | 'move_stage'

export type ViewerActionSnapshot = {
  viewer_id: number
  email: string
  state: 'shortlisted' | 'passed' | 'pending'
  rating: number | null
  flagged: boolean
}

export type DashboardCard = {
  share_id: number
  hm_pitch: string
  shared_at: string
  display_order: number
  display_name: string
  score?: number | null
  latest_action_for_viewer?: 'pending' | 'shortlisted' | 'passed'
  viewer_actions?: ViewerActionSnapshot[]
  whats_new?: '' | 'shared' | 'activity'
  client_stage?: ClientStage
}

export type ClientStage = 'pending' | 'shortlisted' | 'interviewing' | 'passed' | 'offer'

export type DashboardData = {
  client_space: {
    id: number
    name: string
    brand_color: string | null
    brand_logo_url: string | null
    visibility_settings: Record<string, boolean>
  }
  viewer: ClientViewer
  shares: DashboardCard[]
}

export type ShareDetail = {
  share_id: number
  hm_pitch: string
  shared_at: string
  display_name: string
  ai_fit_summary?: {
    summary?: string
    score?: number
    bullets?: Array<{ label: string; rationale: string; match_strength: string }>
    watch_outs?: Array<{ label: string; rationale: string }>
  } | null
  resume?: {
    headline?: string
    summary?: string
    skills?: unknown
    experience?: unknown
  }
  hm_notes?: unknown
  tags?: unknown
  personality?: unknown
  viewer_rating?: number | null
  viewer_flagged?: boolean
  viewer_actions?: ViewerActionSnapshot[]
  urgency_signal?: boolean
  transcript?: string | null
  transcript_meta?: {
    redactions?: Array<{ kind: string; original: string; replacement: string; offset: number }>
  } | null
  redaction_status?: 'pending' | 'processing' | 'done' | 'failed'
  audio_highlights?: AudioHighlightsManifest | null
  video_highlights?: VideoHighlightItem[]
  custom_qa?: CustomQAPair[] | null
}

export type CustomQAPair = {
  question_id: string
  question: string
  answer_text: string
  audio_url?: string
  duration_ms?: number
}

export type AudioHighlightsClip = {
  message_id: number
  start_offset_ms: number
  duration_ms: number
  text_preview: string
  audio_url?: string
}

export type AudioHighlightsManifest = {
  version: number
  clips: AudioHighlightsClip[]
  total_ms: number
}

export type VideoHighlightItem = {
  kind: 'reel' | 'strength' | 'risk' | 'moment'
  caption: string
  duration_ms: number
  video_url: string
  poster_url?: string
}

async function portalFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!r.ok) {
    const text = await r.text()
    throw new Error(`${r.status} ${text}`)
  }
  const text = await r.text()
  if (!text) {
    return undefined as T
  }
  return JSON.parse(text) as T
}

export const clientPortalApi = {
  accept: (token: string) =>
    portalFetch<{ viewer: ClientViewer }>('/api/client-portal/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  signIn: (email: string, password: string) =>
    portalFetch<{ viewer: ClientViewer }>('/api/client-portal/sign-in', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  requestLink: (email: string) =>
    portalFetch<void>('/api/client-portal/request-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  signOut: () => portalFetch<void>('/api/client-portal/sign-out', { method: 'POST' }),
  setPassword: (password: string) =>
    portalFetch<void>('/api/client-portal/set-password', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  me: () => portalFetch<ClientViewer>('/api/client-portal/me'),
  dashboard: () => portalFetch<DashboardData>('/api/client-portal/dashboard'),
  getBranding: (q?: { w?: string | undefined; token?: string | undefined }) => {
    const qs = new URLSearchParams()
    if (q?.w) {
      qs.set('w', q.w)
    }
    if (q?.token) {
      qs.set('token', q.token)
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return portalFetch<PublicBranding>(`/api/client-portal/branding${suffix}`)
  },
  shareDetail: (id: number) => portalFetch<ShareDetail>(`/api/client-portal/shares/${id}`),
  postAction: (shareId: number, action_type: ActionType, payload?: unknown) =>
    portalFetch<void>(`/api/client-portal/shares/${shareId}/actions`, {
      method: 'POST',
      body: JSON.stringify({ action_type, payload }),
    }),
}
